/**
 * PaperCard — cream paper-textured card sitting on the slate.
 *
 * - Cream `surfaces.paper` fill with paperGrain SVG filter for fibre.
 * - Optional washi-tape corners (graphite, ±2deg rotation) pinning the card.
 * - Slight base rotation (default within -1..+2 degrees per instance) so a
 *   stack of cards never reads as machine-aligned.
 * - Drop shadow `0 6px 20px rgba(0,0,0,0.35)` against slate.
 * - 24px content padding.
 */

import { forwardRef, type CSSProperties, type ReactNode, useMemo } from 'react';
import { surfaces } from '@qd/design-tokens';
import { FILTER_IDS } from '../../filters';

interface PaperCardProps {
  children?: ReactNode;
  /** Render two washi-tape strips at the top corners. Default false. */
  tape?: boolean;
  /** Override base rotation in degrees. If omitted a stable -1..+2 deg
   *  value is chosen for the lifetime of this component instance. */
  rotation?: number;
  /** Internal padding. Default 24px. */
  padding?: number | string;
  /** Background color override (still grained). Default cream paper. */
  background?: string;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
}

export const PaperCard = forwardRef<HTMLDivElement, PaperCardProps>(function PaperCard(
  { children, tape = false, rotation, padding = 24, background, className, style, onClick },
  ref,
) {
  // Stable per-instance rotation in [-1, +2] deg if not explicitly set.
  const baseRotation = useMemo(
    () => (typeof rotation === 'number' ? rotation : -1 + Math.random() * 3),
    [rotation],
  );

  return (
    <div
      ref={ref}
      className={className}
      onClick={onClick}
      style={{
        position: 'relative',
        display: 'inline-block',
        background: background ?? surfaces.paper,
        color: surfaces.ink,
        padding,
        borderRadius: 2,
        boxShadow: '0 6px 20px rgba(0,0,0,0.35), 0 1px 0 rgba(0,0,0,0.18)',
        transform: `rotate(${baseRotation.toFixed(2)}deg)`,
        // PaperGrain filter applies feTurbulence multiplied at 8% so the
        // cream surface reads as fibrous instead of flat.
        filter: `url(#${FILTER_IDS.paperGrain})`,
        ...style,
      }}
    >
      {tape && <WashiTape side="left" />}
      {tape && <WashiTape side="right" />}
      {children}
    </div>
  );
});

interface WashiTapeProps {
  side: 'left' | 'right';
}

function WashiTape({ side }: WashiTapeProps) {
  // Each strip rotates the opposite way so the card looks pinned by two
  // independent hands. Slight transparency reads as semi-translucent paper tape.
  const isLeft = side === 'left';
  return (
    <span
      aria-hidden
      style={{
        position: 'absolute',
        top: -10,
        [isLeft ? 'left' : 'right']: -14,
        width: 64,
        height: 22,
        background: '#9C9282',
        opacity: 0.7,
        transform: `rotate(${isLeft ? -2 : 2}deg)`,
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.12)',
        // Slight horizontal stripe for that translucent washi look.
        backgroundImage:
          'linear-gradient(90deg, rgba(255,255,255,0.06) 0 1px, transparent 1px 8px)',
        backgroundSize: '8px 100%',
      }}
    />
  );
}
